/**
 * Big Toe Energy — <bte-video> for the "Média + texte" section.
 *
 * - Lazy loads the video file when it gets close to the screen
 * - Autoplays (muted) only while visible, pauses when scrolled away
 * - Sound button, play/pause overlay, click to play/pause
 * - Playback speed, start time, end time (with loop on the segment)
 * - Respects "reduce motion": no autoplay, a play button is shown instead
 */

const LOAD_MARGIN = '400px 0px';
const VISIBLE_THRESHOLD = 0.25;

class BteVideo extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {IntersectionObserver | null} */
  #loadObserver = null;
  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;
  #userPaused = false;
  #autoPausing = false;
  #wantsAutoplay = false;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    this.#video = video;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#wantsAutoplay = this.dataset.autoplay === 'true' && !reduceMotion;

    if (this.dataset.autoplay === 'true' && reduceMotion) {
      video.removeAttribute('autoplay');
      video.pause();
      this.classList.add('bte-video--show-play');
    }

    video.addEventListener('loadedmetadata', this.#onMetadata);
    video.addEventListener('timeupdate', this.#onTimeUpdate);
    video.addEventListener('play', this.#onPlayState);
    video.addEventListener('pause', this.#onPlayState);
    video.addEventListener('play', this.#onPlay);
    video.addEventListener('pause', this.#onPause);
    video.addEventListener('volumechange', this.#onSoundState);

    this.querySelector('[data-bte-sound]')?.addEventListener('click', this.#toggleSound);
    this.querySelector('[data-bte-play]')?.addEventListener('click', this.#togglePlay);
    if (this.dataset.clickToggle === 'true') video.addEventListener('click', this.#togglePlay);

    if (video.readyState >= 1) this.#onMetadata();
    this.#onPlayState();
    this.#onSoundState();
    this.#observe();
  }

  disconnectedCallback() {
    this.#loadObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    const video = this.#video;
    if (!video) return;
    video.removeEventListener('loadedmetadata', this.#onMetadata);
    video.removeEventListener('timeupdate', this.#onTimeUpdate);
    video.removeEventListener('play', this.#onPlayState);
    video.removeEventListener('pause', this.#onPlayState);
    video.removeEventListener('play', this.#onPlay);
    video.removeEventListener('pause', this.#onPause);
    video.removeEventListener('volumechange', this.#onSoundState);
    video.removeEventListener('click', this.#togglePlay);
  }

  get #start() {
    return Math.max(parseFloat(this.dataset.start || '0') || 0, 0);
  }

  get #end() {
    return Math.max(parseFloat(this.dataset.end || '0') || 0, 0);
  }

  #ensureSource() {
    const video = this.#video;
    if (!video || !video.dataset.src || video.getAttribute('src')) return;
    video.src = video.dataset.src;
    video.preload = 'auto';
    video.load();
  }

  #observe() {
    const video = this.#video;
    if (!video) return;

    if (!('IntersectionObserver' in window)) {
      this.#ensureSource();
      if (this.#wantsAutoplay) this.#play();
      return;
    }

    if (video.dataset.src) {
      this.#loadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.#ensureSource();
            this.#loadObserver?.disconnect();
          }
        },
        { rootMargin: LOAD_MARGIN }
      );
      this.#loadObserver.observe(this);
    }

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (this.#wantsAutoplay && !this.#userPaused) {
              this.#ensureSource();
              this.#play();
            }
          } else if (this.dataset.pauseOffscreen === 'true' && !video.paused) {
            this.#autoPause();
          }
        }
      },
      { threshold: VISIBLE_THRESHOLD }
    );
    this.#visibilityObserver.observe(this);
  }

  #play() {
    const video = this.#video;
    if (!video) return;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => this.classList.add('bte-video--show-play'));
    }
  }

  /** Pause triggered by the script (scrolled away, end time reached), not by the visitor. */
  #autoPause() {
    if (!this.#video) return;
    this.#autoPausing = true;
    this.#video.pause();
  }

  #onPlay = () => {
    this.#userPaused = false;
  };

  #onPause = () => {
    if (this.#autoPausing) {
      this.#autoPausing = false;
      return;
    }
    // Paused by the visitor (native controls, overlay, click): don't restart it on scroll.
    if (this.#video && !this.#video.ended) this.#userPaused = true;
  };

  #onMetadata = () => {
    const video = this.#video;
    if (!video) return;
    const speed = parseFloat(this.dataset.speed || '1') || 1;
    video.defaultPlaybackRate = speed;
    video.playbackRate = speed;
    if (this.#start > 0 && video.currentTime < this.#start) video.currentTime = this.#start;
  };

  #onTimeUpdate = () => {
    const video = this.#video;
    if (!video) return;
    const start = this.#start;
    const end = this.#end;

    if (end > start && video.currentTime >= end) {
      if (video.loop) {
        video.currentTime = start;
      } else {
        this.#autoPause();
      }
      return;
    }

    // Native loop restarts at 0: jump back to the chosen start time.
    if (start > 0 && video.currentTime < start - 0.25) video.currentTime = start;
  };

  #onPlayState = () => {
    const playing = Boolean(this.#video && !this.#video.paused);
    this.classList.toggle('bte-video--playing', playing);
    const button = this.querySelector('[data-bte-play]');
    if (button) button.setAttribute('aria-label', playing ? button.dataset.labelPause || '' : button.dataset.labelPlay || '');
  };

  #onSoundState = () => {
    const muted = Boolean(this.#video?.muted);
    this.classList.toggle('bte-video--muted', muted);
    const button = this.querySelector('[data-bte-sound]');
    if (!button) return;
    button.setAttribute('aria-pressed', String(!muted));
    button.setAttribute('aria-label', muted ? button.dataset.labelOn || '' : button.dataset.labelOff || '');
  };

  #toggleSound = (/** @type {Event} */ event) => {
    event.preventDefault();
    event.stopPropagation();
    const video = this.#video;
    if (!video) return;
    this.#ensureSource();
    video.muted = !video.muted;
    if (!video.muted && video.paused) {
      this.#userPaused = false;
      this.#play();
    }
  };

  #togglePlay = (/** @type {Event} */ event) => {
    event.preventDefault();
    const video = this.#video;
    if (!video) return;
    this.#ensureSource();
    if (video.paused) {
      this.#userPaused = false;
      this.classList.remove('bte-video--show-play');
      this.#play();
    } else {
      this.#userPaused = true;
      video.pause();
    }
  };
}

if (!customElements.get('bte-video')) customElements.define('bte-video', BteVideo);
